import React from "react";
import { Navigate, generatePath, useParams } from "react-router";
import {
  FULL_GROUPS_PATH,
  FULL_GROUP_POLICIES_PATH,
  FULL_LICENSING_PATH,
  FULL_MEMBERS_PATH,
  FULL_ORGANIZATION_POLICIES_PATH,
  FULL_SINGLE_GROUP_PATH,
  OLD_DIFF_REPORT_PATH,
  OLD_GROUPS_PATH,
  OLD_GROUP_CONFIGURATION_PATH,
  OLD_LICENSING_PATH,
  OLD_MEMBERS_PATH,
  OLD_ORGANIZATION_CONFIGURATION_PATH,
  OLD_SINGLE_GROUP_PATH,
  PAGE_NOT_FOUND_ROUTE,
} from "Constants";
import { useCurrentOrganization } from "Hooks";
import RedirectToNewDiffRoute from "./RedirectToNewDiffRoute";

type AllowedParams = "organizationId" | "groupId" | "membersGroupId";

type RedirectWithOrgAndGroupIdsProps = {
  pathTemplate: string;
  requiredParams: AllowedParams[];
};

const RedirectWithOrgAndGroupIds = ({
  pathTemplate,
  requiredParams,
}: RedirectWithOrgAndGroupIdsProps) => {
  const routeParams = useParams();
  const organizationId = useCurrentOrganization()?.id;

  const params: Record<string, string | undefined> = {};

  for (const key of requiredParams) {
    if (key === "organizationId") {
      params[key] = organizationId;
    } else {
      params[key] = routeParams[key];
    }
  }

  const missingParam = Object.entries(params).find(
    ([, param]) => param === undefined,
  );

  if (missingParam) {
    return <Navigate to={`/${PAGE_NOT_FOUND_ROUTE}`} replace />;
  }

  const target = generatePath(pathTemplate, params as Record<string, string>);
  return <Navigate to={`/${target}`} replace />;
};

export default RedirectWithOrgAndGroupIds;

type BackwardsCompatibleRoutes = {
  path: string;
  element: React.ReactNode;
};

export const generateBackwardsCompatibleRoutes =
  (): BackwardsCompatibleRoutes[] => [
    {
      path: OLD_MEMBERS_PATH,
      element: (
        <RedirectWithOrgAndGroupIds
          pathTemplate={FULL_MEMBERS_PATH}
          requiredParams={["organizationId"]}
        />
      ),
    },
    {
      path: OLD_GROUPS_PATH,
      element: (
        <RedirectWithOrgAndGroupIds
          pathTemplate={FULL_GROUPS_PATH}
          requiredParams={["organizationId"]}
        />
      ),
    },
    {
      path: OLD_SINGLE_GROUP_PATH,
      element: (
        <RedirectWithOrgAndGroupIds
          pathTemplate={FULL_SINGLE_GROUP_PATH}
          requiredParams={["organizationId", "membersGroupId"]}
        />
      ),
    },
    {
      path: OLD_ORGANIZATION_CONFIGURATION_PATH,
      element: (
        <RedirectWithOrgAndGroupIds
          pathTemplate={FULL_ORGANIZATION_POLICIES_PATH}
          requiredParams={["organizationId"]}
        />
      ),
    },
    {
      path: OLD_GROUP_CONFIGURATION_PATH,
      element: (
        <RedirectWithOrgAndGroupIds
          pathTemplate={FULL_GROUP_POLICIES_PATH}
          requiredParams={["organizationId", "groupId"]}
        />
      ),
    },
    {
      path: OLD_LICENSING_PATH,
      element: (
        <RedirectWithOrgAndGroupIds
          pathTemplate={FULL_LICENSING_PATH}
          requiredParams={["organizationId"]}
        />
      ),
    },
    {
      path: OLD_DIFF_REPORT_PATH,
      element: <RedirectToNewDiffRoute />,
    },
  ];
